import React, { createContext, useContext, useEffect, useState } from "react"
import clsx from "clsx"
import { twMerge } from "tailwind-merge"
import { motion } from "framer-motion"

const ToggleContext = createContext<{
  value: string | null
  setValue: React.Dispatch<React.SetStateAction<string | null>>
  onChange?: (value: string) => void
} | null>(null)

interface ToggleProps {
  children: React.ReactNode
  name?: string
  size?: "small" | "medium" | "large"
  style?: React.CSSProperties
  onChange?: (value: string) => void
  defaultValue?: string
  className?: string
}

export const Toggle = ({
  children,
  name = "default",
  size = "medium",
  style,
  onChange,
  defaultValue,
  className,
}: ToggleProps) => {
  const [value, setValue] = useState<string | null>(defaultValue || null)

  // Use useEffect to handle onChange when value changes
  useEffect(() => {
    if (value && onChange) {
      onChange(value)
    }
  }, [value, onChange])

  return (
    <ToggleContext.Provider value={{ value, setValue, onChange }}>
      <div
        className={clsx(
          "bg-background-100 border-gray-alpha-400 flex border p-1",
          size === "small" && "h-8 rounded-md",
          size === "medium" && "h-10 rounded-md",
          size === "large" && "h-12 rounded-lg",
          className,
        )}
        style={style}
      >
        {React.Children.map(children, (child) =>
          React.cloneElement(child as React.ReactElement<ToggleControlProps>, {
            size,
            name,
          }),
        )}
      </div>
    </ToggleContext.Provider>
  )
}

interface ToggleControlProps {
  label?: string
  value: string
  defaultChecked?: boolean
  disabled?: boolean
  name?: string
  size?: "small" | "medium" | "large"
  icon?: React.ReactNode
}

const ToggleControl = ({
  label,
  value,
  defaultChecked,
  disabled = false,
  name,
  size = "medium",
  icon,
}: ToggleControlProps) => {
  const context = useContext(ToggleContext)
  const checked = value === context?.value

  useEffect(() => {
    if (defaultChecked && context) {
      context.setValue(value)
    }
  }, [defaultChecked, value, context])

  return (
    <label
      className={clsx(
        "relative flex h-full flex-1",
        disabled && "pointer-events-none cursor-not-allowed",
      )}
      onClick={() => context?.setValue(value)}
    >
      <input
        type="radio"
        name={name}
        value={value}
        defaultChecked={defaultChecked}
        disabled={disabled}
        checked={checked}
        className="hidden"
      />
      <span
        className={twMerge(
          clsx(
            "flex flex-1 cursor-pointer items-center justify-center font-sans font-medium duration-150",
            checked
              ? "text-black dark:text-white"
              : "hover:text-gray-1000 hover:fill-gray-1000",
            disabled && "fill-gray-800 text-gray-800",
            !icon && size === "small" && "px-3 text-sm",
            !icon && size === "medium" && "px-3 text-sm",
            !icon && size === "large" && "px-4 text-base",
            icon && size === "small" && "px-2 py-1",
            icon && size === "medium" && "px-3 py-2",
            icon && size === "large" && "p-3",
          ),
        )}
      >
        {icon ? (
          <span className={clsx(size === "large" && "scale-125")}>{icon}</span>
        ) : (
          label
        )}
      </span>
      {checked && (
        <motion.span
          layoutId="selected"
          className="absolute inset-0 z-10 rounded-md bg-gray-900/50 mix-blend-difference dark:bg-accent"
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
    </label>
  )
}

Toggle.Control = ToggleControl
